import { FileText, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { TEMPLATE_ACCEPT } from '@/features/admin/model';

interface AttachedFileFieldProps {
  existingName: string | null;
  file: File | null;
  removeFile: boolean;
  disabled?: boolean;
  onFile: (file: File | null) => void;
  onRemoveFile: (remove: boolean) => void;
}

/** Optional PDF or Word attachment for a license or contract. */
export function AttachedFileField({
  existingName,
  file,
  removeFile,
  disabled,
  onFile,
  onRemoveFile,
}: AttachedFileFieldProps) {
  const showingExisting = !!existingName && !file && !removeFile;
  return (
    <div className="space-y-2">
      <Label htmlFor="admin-record-file">Document</Label>
      {showingExisting && (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 text-sm">
          <span className="flex min-w-0 items-center gap-2">
            <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{existingName}</span>
          </span>
          {!disabled && (
            <Button type="button" variant="ghost" size="sm" onClick={() => onRemoveFile(true)}>
              <X className="h-3.5 w-3.5" />
              Remove
            </Button>
          )}
        </div>
      )}
      {removeFile && !file && (
        <p className="text-xs text-muted-foreground">
          The current document will be removed when you save.{' '}
          {!disabled && (
            <button type="button" className="underline" onClick={() => onRemoveFile(false)}>
              Keep it
            </button>
          )}
        </p>
      )}
      {!disabled && (
        <input
          id="admin-record-file"
          type="file"
          accept={TEMPLATE_ACCEPT}
          className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-lg file:border-0 file:bg-secondary file:px-3 file:py-2 file:text-sm file:font-medium file:text-foreground"
          onChange={(event) => {
            const next = event.target.files?.[0] ?? null;
            onFile(next);
            if (next) onRemoveFile(false);
          }}
        />
      )}
      {file && <p className="text-xs text-muted-foreground">Selected: {file.name}</p>}
    </div>
  );
}
